import type Signal from "app/pages/sales-intelligence/sub-modules/signals/models/core/Signal";
import type { ComponentType } from "react";
import { useDispatch, useSelector } from "react-redux";
import { isActionOf } from "typesafe-actions";

import type SignalFilters from "../models/core/SignalFilters";
import type SignalUpdateDTO from "../models/miscellaneous/SignalUpdateDTO";
import { updateSignalAsync } from "../store/action-creators";
import { updateSignalThunk } from "../store/effects";
import { selectSignalUpdateIsInProgress } from "../store/selectors";

type HoCProps = {
    onUpdateSuccess?<F extends SignalFilters>(signal: Signal<F>): void;
};

type ConsumerProps = {
    isSubmitInProgress: boolean;
    onUpdateSubmit<F extends SignalFilters>(dto: SignalUpdateDTO<F>): void;
};

export default function withSignalUpdateSubmit<P extends ConsumerProps>(
    Consumer: ComponentType<P>,
): ComponentType<Omit<P, "isSubmitInProgress" | "onUpdateSubmit"> & HoCProps> {
    return function WrappedWithSignalUpdateSubmit(props: Readonly<P> & HoCProps) {
        const { onUpdateSuccess, ...rest } = props;
        const dispatch = useDispatch();
        const isSubmitInProgress = useSelector(selectSignalUpdateIsInProgress);

        async function updateSignal<F extends SignalFilters>(dto: SignalUpdateDTO<F>) {
            const resultAction = await dispatch(updateSignalThunk(dto));
            const isSuccessfulAction = isActionOf(updateSignalAsync.success);

            if (isSuccessfulAction(resultAction)) {
                onUpdateSuccess?.(resultAction.payload);
            }
        }

        return (
            <Consumer
                {...(rest as P)}
                isSubmitInProgress={isSubmitInProgress}
                onUpdateSubmit={updateSignal}
            />
        );
    };
}
